import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Case } from "../shared/CaseModel";
import { toCaseParameters } from "../shared/caseExport";
import { encryptConfig } from "./caseCrypto";

const MIN_PASSWORD_LENGTH = 8;

function sanitizeFilename(name: string): string {
  const base = name
    .trim()
    .replace(/[^\p{L}\p{N}_-]+/gu, "_")
    .replace(/^_+|_+$/g, "");
  return base.length > 0 ? base : "case";
}

function downloadJson(filename: string, content: string) {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ExportCaseDialog = ({
  caseToExport,
  open,
  onClose,
}: {
  caseToExport: Case;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "CaseOverview.exportDialog",
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string>();

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canExport =
    password.length >= MIN_PASSWORD_LENGTH && password === confirm;

  const close = () => {
    setPassword("");
    setConfirm("");
    setError(undefined);
    setExporting(false);
    onClose();
  };

  const exportCase = async () => {
    if (!canExport) return;
    setExporting(true);
    setError(undefined);
    try {
      const config = toCaseParameters(caseToExport);
      const file = await encryptConfig(config, password);
      downloadJson(
        `${sanitizeFilename(caseToExport.name)}.casesolv`,
        JSON.stringify(file, null, 2),
      );
      close();
    } catch (e) {
      console.warn("Failed to export case", e);
      setError(t("error"));
      setExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={exporting ? undefined : close}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{
            mb: 2,
          }}
        >
          {t("description")}
        </DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          label={t("password")}
          type="password"
          fullWidth
          variant="outlined"
          value={password}
          disabled={exporting}
          error={tooShort}
          helperText={
            tooShort ? t("tooShort", { min: MIN_PASSWORD_LENGTH }) : undefined
          }
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          required
          margin="dense"
          label={t("confirmPassword")}
          type="password"
          fullWidth
          variant="outlined"
          value={confirm}
          disabled={exporting}
          error={mismatch}
          helperText={mismatch ? t("mismatch") : undefined}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && (
          <Alert sx={{ mt: 2 }} severity="error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={exporting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          endIcon={<FileDownloadIcon />}
          disabled={!canExport || exporting}
          loading={exporting}
          onClick={exportCase}
        >
          {t("download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
